/**
 * Declares that a GPU resource a component built by hand is that component's to release.
 *
 * React Three Fiber disposes the three.js objects *it* creates from JSX, but a
 * `CanvasTexture` built inside a `useMemo` was never its to know about. While the game was
 * one level for its whole run that cost nothing — the resources lived as long as the page
 * did. With levels swapping at every portal it is a leak per crossing, and the machine it
 * fails on first is a phone, three mazes in.
 *
 * Deliberately separate from the memo that builds the value rather than wrapping it: a
 * hook that forwarded a dependency list would hide those dependencies from the compiler
 * and from the lint rules that check them, and the memo is where they belong.
 *
 * Only the value handed over is released, along with the values of a record or array of
 * them. Anything merely *reached through* one — a shared material's gradient map, say — is
 * out of scope by construction, which is what keeps the module-level caches in
 * `materials.ts` safe from this.
 */

import { useEffect } from 'react';

type Disposable = { dispose: () => void };

function isDisposable(value: unknown): value is Disposable {
  return (
    typeof value === 'object' && value !== null && typeof (value as Disposable).dispose === 'function'
  );
}

function disposeAll(built: unknown): void {
  if (isDisposable(built)) {
    built.dispose();
    return;
  }
  // `MazeGrounds` builds its water maps as one memo returning several textures, and a
  // cleanup that understood only a single value would leak all but one of them.
  if (typeof built === 'object' && built !== null) {
    for (const value of Object.values(built)) if (isDisposable(value)) value.dispose();
  }
}

export function useDisposable<T>(built: T): T {
  // The release happens in an effect rather than during render: disposing a texture that a
  // half-committed render is still holding is exactly the kind of thing that only
  // misbehaves under Strict Mode and in production.
  useEffect(() => () => disposeAll(built), [built]);
  return built;
}
